import { useAuthStore } from '@revelio/auth';
import { TaxonomyType } from '@revelio/data-access';

import { useGeographyTaxonomyTree } from './hooks/useGeographyTaxonomyTree';
import { useTaxonomyList } from './hooks/useTaxonomyList';

/**
 * One taxonomy's tree, fetched and thrown away. Its own component so the warm-up
 * can cover a variable-length taxonomy list without calling a hook in a loop.
 */
const WarmGeographyTaxonomyTree = ({ taxonomyId }: { taxonomyId: number }) => {
  useGeographyTaxonomyTree({
    taxonomyId,
    // Default `low` priority: these queue behind the page's own data queries,
    // so warming never delays a chart.
  });
  return null;
};

/**
 * Pulls every geography taxonomy tree into the urql cache at app start.
 *
 * The trees are large — the administrative one carries every US county — and
 * they were previously fetched only when the filter tree or the preferences
 * modal first needed them, so opening the picker or switching taxonomy paid the
 * full round-trip. Warming here makes both instant, at the cost of two
 * low-priority requests per session for users who never open a geography filter.
 *
 * Renders nothing; mount it once inside the urql provider.
 */
export const GeographyTaxonomyWarmUp = () => {
  // This component is mounted alongside the router, OUTSIDE every RequireAuth
  // gate, so without this check it fires authenticated queries on the login
  // page for logged-out users — round-trips that only 401.
  const isLoggedIn = useAuthStore((state) => state.user !== undefined);

  const { taxonomies } = useTaxonomyList({
    taxonomyType: TaxonomyType.Geography,
    pause: !isLoggedIn,
  });

  if (!isLoggedIn) return null;

  return (
    <>
      {taxonomies.map((taxonomy) => (
        <WarmGeographyTaxonomyTree key={taxonomy.id} taxonomyId={taxonomy.id} />
      ))}
    </>
  );
};
